import { mockState } from '@/api/mock-data'
import { USE_MOCK } from '@/config'
import type { Profile } from '@/types/user'
import { createClient } from '@/api/supabase'
import i18n from '@/utils/i18n'
import { isPartner } from '@/utils/access'

function translateAuthError(message: string): string {
  const m = message.trim()
  const lower = m.toLowerCase()
  if (lower.includes('invalid login credentials')) {
    return i18n.t('apiErr.auth.invalidCredentials')
  }
  if (lower.includes('email not confirmed')) {
    return i18n.t('apiErr.auth.notConfirmed')
  }
  if (lower.includes('too many requests')) {
    return i18n.t('apiErr.auth.tooManyAttempts')
  }
  // Заблокированный пользователь: при `auth.users.banned_until = 'infinity'`
  // GoTrue падает на запросе схемы и возвращает «Database error querying schema»;
  // другие версии присылают «user is banned» / «user_banned». В обоих случаях
  // показываем понятный текст про блокировку, а не «ошибку БД».
  if (
    lower.includes('user is banned') ||
    lower.includes('user_banned') ||
    lower.includes('database error querying schema')
  ) {
    return i18n.t('apiErr.auth.blocked')
  }
  if (lower.includes('network') || lower.includes('fetch')) {
    return i18n.t('apiErr.auth.network')
  }
  return m || i18n.t('apiErr.auth.generic')
}

/** Mock: любой пароль подходит для демо; для сид-пользователей можно использовать Demo1234 */
export async function signInWithPhonePassword(
  phone: string,
  password: string,
): Promise<Profile> {
  if (USE_MOCK) {
    const normalized = phone.replace(/\D/g, '')
    const p = mockState.profiles.find(
      (u) => u.phone.replace(/\D/g, '') === normalized,
    )
    if (!p) throw new Error(i18n.t('apiErr.auth.invalidCredentials'))
    if (!password) throw new Error(i18n.t('apiErr.auth.enterPassword'))
    if (!p.is_active) throw new Error(i18n.t('apiErr.auth.blocked'))

    // Сотрудники Стрижа и партнёры (кабинет /partner) — всегда разрешены
    if (p.role === 'admin' || p.role === 'dispatcher' || isPartner(p)) {
      return p
    }

    // Менеджер / руководитель заказчика — разрешены только для customer-компании
    if (p.role === 'manager' || p.role === 'customer_supervisor') {
      const company = p.company_id
        ? mockState.companies.find((c) => c.id === p.company_id)
        : null
      if (company?.type === 'customer') {
        return { ...p, company_type: 'customer' }
      }
    }

    throw new Error(i18n.t('auth.noAccess'))
  }
  // Auth uses email derived from phone (no phone provider needed in Supabase)
  const authEmail = `${phone.replace(/\D/g, '')}@stryzh.app`
  const supabase = createClient()
  const { data, error } = await supabase.auth.signInWithPassword({ email: authEmail, password })
  if (error) throw new Error(translateAuthError(error.message))
  if (!data.user) throw new Error(i18n.t('apiErr.auth.noUserData'))

  const { data: profile, error: profileErr } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', data.user.id)
    .single()

  if (profileErr || !profile) throw new Error(i18n.t('apiErr.auth.profileNotFound'))
  if (!profile.is_active) {
    await supabase.auth.signOut()
    throw new Error(i18n.t('apiErr.auth.blocked'))
  }

  // Only staff, customer manager/customer_supervisor can access admin panel
  const role = profile.role as string
  // Партнёр видит только кабинет /partner; доступ к данным режет шлюз в БД
  // (check_user_active) — компания без воронки получит «нет доступа» там же.
  if (role === 'admin' || role === 'dispatcher' || isPartner(profile as unknown as Profile)) {
    return profile as unknown as Profile
  }

  if (role === 'manager' || role === 'customer_supervisor') {
    let companyType: string | null | undefined = null
    if (profile.company_id) {
      const { data: company } = await supabase
        .from('companies')
        .select('type')
        .eq('id', profile.company_id)
        .maybeSingle()
      companyType = (company as { type?: string } | null)?.type
    }
    if (companyType === 'customer') {
      return { ...(profile as unknown as Profile), company_type: 'customer' }
    }
  }

  // Sign out the user since they don't have access
  await supabase.auth.signOut()
  throw new Error(i18n.t('auth.noAccess'))
}

export async function signOut(): Promise<void> {
  if (USE_MOCK) return
  const supabase = createClient()
  await supabase.auth.signOut()
}

export async function updatePassword(newPassword: string): Promise<void> {
  if (USE_MOCK) return
  const supabase = createClient()
  const { error } = await supabase.auth.updateUser({ password: newPassword })
  if (error) throw error
}
